import React, { useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import { Question } from '../../../utils/types/assessment_types';

type UploadMediaProps = {
  question: Question;
  addFileViaInput: (event: React.ChangeEvent<HTMLInputElement>) => void;
  clearMediaFile: () => void;
};

export const UploadMedia = ({
  question,
  addFileViaInput,
  clearMediaFile,
}: UploadMediaProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const checkMediaType = () => {
    return !question.mediaExtension?.match(/^(mp4|mkv|avi|webm)$/);
  };

  const renderMediaInput = () => {
    if (question.mediaQuestion) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, my: 1.5 }}>
          <Typography
            sx={{
              fontSize: '0.6875rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'text.secondary',
            }}
          >
            Attached Media File
          </Typography>
          <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: 500, borderRadius: '12px', overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
            {checkMediaType() ? (
              <Box
                component="img"
                src={question.fileEncoded || question.fileUrl || ''}
                alt={`${question.id} Media`}
                sx={{ width: '100%', maxHeight: 300, objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <Box
                component="video"
                controls
                src={question.fileEncoded || question.fileUrl || ''}
                sx={{ width: '100%', maxHeight: 300, display: 'block' }}
              />
            )}
            <IconButton
              size="small"
              onClick={clearMediaFile}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(0, 0, 0, 0.6)',
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, gap: 1.5, my: 1.5 }}>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          startIcon={<CloudUploadRoundedIcon sx={{ fontSize: 18 }} />}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8125rem',
            px: 2,
            whiteSpace: 'nowrap',
          }}
        >
          Upload Media
        </Button>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          Supported: mp4, mkv, avi, webm, jpg, jpeg, png, gif. Max size: 20MB
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <input
        type="file"
        onChange={addFileViaInput}
        ref={fileInputRef}
        style={{ display: 'none' }}
      />
      {renderMediaInput()}
    </>
  );
};
